import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tooltip, Modal } from "@mui/material";
import { FaArrowLeft, FaTimes } from "react-icons/fa";
import "../styles/ViewChart.scss";
import { useAtomValue, useSetAtom } from "jotai";
import { tripAtom } from "../components/JotaiStates/commonStateAtoms";
import { alertAtom, globalLoaderAtom } from "../jotaiState/globalStateAtoms";
import useLogin from "../components/common/useLogin";
import { apiGet } from "../helper/utils";
import { BookingStatusOfChart } from "../helper/apiUrls";

export const ViewChart = () => {
  const navigate = useNavigate();
  const trip = useAtomValue(tripAtom);
  const { login } = useLogin();
  const setAlert = useSetAtom(alertAtom);
  const setLoader = useSetAtom(globalLoaderAtom);

  const [seatChart, setSeatChart] = useState([]);
  const [bookedMap, setBookedMap] = useState({});
  const [chartInfo, setChartInfo] = useState({ totalSeats: 0, isChartCancel: false });
  const [selectedBooking, setSelectedBooking] = useState(null);

  const getChartData = () => {
    setLoader(true);
    const chartDateObj = new Date(trip?.ChartDate);
    const payload = {
      companyChartID: trip?.companychartid,
      tripID: trip?.TripID,
      month: chartDateObj.getMonth() + 1,
      day: chartDateObj.getDate(),
      year: chartDateObj.getFullYear(),
      fromCityID: trip?.FromCityId,
      toCityID: trip?.ToCityId,
      timeOFTrip: trip?.TripTime,
      intUserID: login?.UserID,
      intcompanyID: login?.CompanyID,
      intAgentID: 0,
      strEntityType: "U",
      blnIsQuotaBranch: false,
      intBranchID: 0,
      arrivalTimeOfTrip: trip?.ArrivalTime,
      chartDate: trip?.ChartDate?.split(" ")[0],
    };
    apiGet(BookingStatusOfChart, payload)
      .then((res) => {
        if (!res.status) {
          throw new Error(res.Message || "Error fetching chart");
        }
        const data = JSON.parse(res.Data);
        const map = {};
        (data.Table || []).forEach((seat) => {
          if (seat.SeatNo) map[seat.SeatNo] = seat;
        });
        setBookedMap(map);
        setSeatChart(data.NewSeatChart || []);
        setChartInfo({
          totalSeats: data.Table1?.[0]?.TotalSeats,
          isChartCancel: data.Table1?.[0]?.IsChartCancel === 1,
        });
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message, nextId: "" });
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (trip?.TripID) {
      getChartData();
    }
  }, [trip]);

  const headerLabels = useMemo(() => {
    const headerStr = seatChart[0]?.Header || "";
    return headerStr
      .split(",")
      .map((v) => (v.trim() === "2" ? "UP" : v.trim() === "1" ? "DN" : ""));
  }, [seatChart]);

  const maxCol = useMemo(
    () =>
      seatChart.length
        ? Math.max(...seatChart.map((c) => c.ColumnNo)) + 1
        : 0,
    [seatChart]
  );

  const seatCells = useMemo(
    () => seatChart.filter((cell) => cell.SeatType !== "aisle"),
    [seatChart]
  );

  const bookedCount = seatCells.filter((c) => bookedMap[c.SeatLabel]).length;
  const totalSeatCount = chartInfo.totalSeats ?? seatCells.length;
  const availableCount = totalSeatCount - bookedCount;

  const seatInfoRows = selectedBooking
    ? [
      {
        label: "Name",
        value: [selectedBooking.PaxName, selectedBooking.PassengerContactNo1]
          .filter(Boolean)
          .join(" - "),
      },
      { label: "Sector", value: selectedBooking.RouteName },
      {
        label: "Total",
        value: `${selectedBooking.Fare} (${selectedBooking.Fare} +${selectedBooking.STax || 0}GST-${selectedBooking.Disc || 0}Disc)`,
      },
      {
        label: "PNR/Ticket No",
        value: [selectedBooking.BookingID, selectedBooking.TicketNo]
          .filter(Boolean)
          .join("/"),
      },
      {
        label: "User",
        value: [selectedBooking.UserBooked, selectedBooking.BranchBooked]
          .filter(Boolean)
          .join(" / "),
      },
      { label: "Booking Date", value: selectedBooking.BookingDateFormated },
      {
        label: "Pickup",
        value: [selectedBooking.PickupName, selectedBooking.PickupDate]
          .filter(Boolean)
          .join(" /"),
      },
      {
        label: "Dropoff",
        value: [selectedBooking.DropoffName, selectedBooking.DropoffDate]
          .filter(Boolean)
          .join(" /"),
      },
      { label: "Remarks", value: selectedBooking.Remarks },
      {
        label: "Seats",
        value: `${selectedBooking.AllSeats || selectedBooking.SeatNo} - Total:${selectedBooking.TotalFare}(${selectedBooking.TotalFare} +${selectedBooking.STax || 0}GST-${selectedBooking.Disc || 0}Disc)`,
      },
    ]
    : [];

  if (!trip) {
    return <div>No Trip Data Found</div>;
  }

  return (
    <Box className="view-chart-container">
      <Box className="trip-details-header">
        <FaArrowLeft className="back-icon" onClick={() => navigate(-1)} />
        <div>
          <div className="route-text">{trip.RouteName}</div>
          <div className="date-text">{trip.ChartDate}</div>
        </div>
      </Box>

      {chartInfo.isChartCancel && (
        <Typography className="chart-cancel-banner">Chart Cancelled</Typography>
      )}

      <Box className="seat-legend">
        <Box className="legend-item">
          <span className="legend-swatch vacant" /> Vacant
        </Box>
        <Box className="legend-item">
          <span className="legend-swatch booked" /> Booked
        </Box>
      </Box>

      <Box className="chart-summary">
        <span>Total : {totalSeatCount}</span>
        <span>Booked : {bookedCount}</span>
        <span>Avl : {availableCount}</span>
      </Box>

      <Box className="deck-block">
        <Box
          className="seat-grid-header"
          sx={{ gridTemplateColumns: `repeat(${maxCol},32px)` }}
        >
          {headerLabels.map((label, idx) => (
            <Box key={idx} className="header-cell" sx={{ gridColumn: idx + 1 }}>
              {label}
            </Box>
          ))}
        </Box>
        <Box
          className="seat-grid"
          sx={{ gridTemplateColumns: `repeat(${maxCol},32px)` }}
        >
          {seatCells.map((cell) => {
            const booking = bookedMap[cell.SeatLabel];
            const isBooked = !!booking;
            const fare = cell.dcTripFare ? Math.round(cell.dcTripFare) : null;
            return (
              <Tooltip
                key={cell.ChartLayoutID}
                title={
                  isBooked
                    ? booking.PaxName || "Booked"
                    : `Seat ${cell.SeatLabel} - Vacant`
                }
              >
                <Box
                  className={`seat-cell ${cell.SeatType} ${isBooked ? "booked" : "vacant"
                    }`}
                  sx={{
                    gridColumn: cell.ColumnNo + 1,
                    gridRow: `${cell.RowNo + 1} / span ${cell.Height}`,
                  }}
                  onClick={() => isBooked && setSelectedBooking(booking)}
                >
                  <span className="seat-number">{cell.SeatLabel}</span>
                  {fare != null && <span className="seat-fare">{fare}</span>}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <Modal open={!!selectedBooking} onClose={() => setSelectedBooking(null)}>
        <Box className="seat-info-modal">
          <Box className="seat-info-header">
            <Typography className="seat-info-title" sx={{ color: "#ffffff" }}>
              Seat Info
            </Typography>
            <FaTimes
              className="seat-info-close"
              onClick={() => setSelectedBooking(null)}
            />
          </Box>
          <Box className="seat-info-body">
            {seatInfoRows.map(
              ({ label, value }) =>
                value && (
                  <Box key={label} className="seat-info-row">
                    <span className="seat-info-label">{label}</span>
                    <span className="seat-info-colon">:</span>
                    <span className="seat-info-value">{value}</span>
                  </Box>
                )
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
